import {
  GROUND_MAX_ANGLE_TOLERANCE,
  GROUND_MAX_RATIO,
  GROUND_MIN_RATIO,
} from '../components/constants';

export interface Point2D {
  x: number;
  y: number;
}

export interface GPSPoint {
  latitude: number;
  longitude: number;
}

export interface RectangleOptions {
  /** Allowed deviation from 90° per corner angle. Default: 2. */
  toleranceDeg?: number;
  /** Minimum aspect ratio (long side / short side). Default: 1/3. */
  minRatio?: number;
  /** Maximum aspect ratio (long side / short side). Default: 3. */
  maxRatio?: number;
}

type ThreeOrFour<T> = [T, T, T] | [T, T, T, T];

// --- internal helpers ---

const dot = (v1: Point2D, v2: Point2D): number => v1.x * v2.x + v1.y * v2.y;

const magnitude = (v: Point2D): number => Math.sqrt(v.x * v.x + v.y * v.y);

const subtract = (a: Point2D, b: Point2D): Point2D => ({ x: a.x - b.x, y: a.y - b.y });

const cross2D = (O: Point2D, A: Point2D, B: Point2D): number =>
  (A.x - O.x) * (B.y - O.y) - (A.y - O.y) * (B.x - O.x);

/**
 * Returns the convex hull of `points` as an ordered CCW polygon.
 * Uses the Andrew monotone chain algorithm (O(n log n)).
 */
const convexHull = (points: Point2D[]): Point2D[] => {
  const sorted = [...points].sort((a, b) => a.x !== b.x ? a.x - b.x : a.y - b.y);
  const lower: Point2D[] = [];
  for (const p of sorted) {
    while (lower.length >= 2 && cross2D(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop();
    lower.push(p);
  }
  const upper: Point2D[] = [];
  for (let i = sorted.length - 1; i >= 0; i--) {
    const p = sorted[i];
    while (upper.length >= 2 && cross2D(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop();
    upper.push(p);
  }
  lower.pop();
  upper.pop();
  return lower.concat(upper);
};

const angleDegAt = (a: Point2D, b: Point2D, c: Point2D): number => {
  const v1 = subtract(a, b);
  const v2 = subtract(c, b);
  const magV1 = magnitude(v1);
  const magV2 = magnitude(v2);
  if (magV1 === 0 || magV2 === 0) return 0;
  const cosAngle = Math.min(1, Math.max(-1, dot(v1, v2) / (magV1 * magV2)));
  return (Math.acos(cosAngle) * 180) / Math.PI;
};

const inferFourthPoint = (p1: Point2D, p2: Point2D, p3: Point2D): Point2D => {
  // Try all 3 parallelogram completions; pick the one where the hull has 4 vertices
  // (i.e. no point is inside the triangle of the others)
  const candidates: Point2D[] = [
    { x: p1.x + p2.x - p3.x, y: p1.y + p2.y - p3.y },
    { x: p1.x + p3.x - p2.x, y: p1.y + p3.y - p2.y },
    { x: p2.x + p3.x - p1.x, y: p2.y + p3.y - p1.y },
  ];

  let bestCandidate = candidates[0];
  let bestScore = Infinity;

  for (const candidate of candidates) {
    const hull = convexHull([p1, p2, p3, candidate]);
    if (hull.length !== 4) continue; // candidate is inside the triangle — skip
    const score = hull.reduce((sum, _, i) => {
      const prev = hull[(i + hull.length - 1) % hull.length];
      const next = hull[(i + 1) % hull.length];
      return sum + Math.abs(angleDegAt(prev, hull[i], next) - 90);
    }, 0);
    if (score < bestScore) {
      bestScore = score;
      bestCandidate = candidate;
    }
  }

  return bestCandidate;
};

const getSideLengths = (hull: Point2D[]): [number, number] => {
  const dist = (a: Point2D, b: Point2D) => Math.hypot(b.x - a.x, b.y - a.y);
  const side0 = dist(hull[0], hull[1]);
  const side1 = dist(hull[1], hull[2]);
  const side2 = dist(hull[2], hull[3]);
  const side3 = dist(hull[3], hull[0]);
  return [(side0 + side2) / 2, (side1 + side3) / 2];
};

const checkIsRectangle = (
  points: Point2D[],
  toleranceDeg: number,
  minRatio: number,
  maxRatio: number,
): boolean => {
  const fourPoints =
    points.length === 3
      ? [...points, inferFourthPoint(points[0], points[1], points[2])]
      : [...points];

  const hull = convexHull(fourPoints);

  // If the hull has fewer than 4 vertices, one point lies inside the triangle
  // of the other three → not a valid (convex) quadrilateral.
  if (hull.length !== 4) return false;

  // Check all interior angles are ~90°
  for (let i = 0; i < 4; i++) {
    const prev = hull[(i + 3) % 4];
    const curr = hull[i];
    const next = hull[(i + 1) % 4];
    if (Math.abs(angleDegAt(prev, curr, next) - 90) > toleranceDeg) return false;
  }

  // Check aspect ratio
  const [sideA, sideB] = getSideLengths(hull);
  if (sideA === 0 || sideB === 0) return false;
  const ratio = Math.max(sideA, sideB) / Math.min(sideA, sideB);
  if (ratio < minRatio || ratio > maxRatio) return false;

  return true;
};

const gpsToMetric = (origin: GPSPoint, point: GPSPoint): Point2D => {
  const EARTH_RADIUS_M = 6_371_000;
  const latRad = (origin.latitude * Math.PI) / 180;
  return {
    x: (point.longitude - origin.longitude) * (Math.PI / 180) * EARTH_RADIUS_M * Math.cos(latRad),
    y: (point.latitude - origin.latitude) * (Math.PI / 180) * EARTH_RADIUS_M,
  };
};

// --- public API ---

/**
 * Returns true when the given 3 or 4 **local** `{ x, y }` points describe a rectangle
 * whose aspect ratio is between `minRatio` and `maxRatio`.
 *
 * @param points  3 or 4 `{ x, y }` coordinate pairs (any order).
 * @param options toleranceDeg (default 2), minRatio (default 1/3), maxRatio (default 3).
 */
export const isRectangle = (
  points: ThreeOrFour<Point2D>,
  options: RectangleOptions = {},
): boolean => {
  const {
    toleranceDeg = GROUND_MAX_ANGLE_TOLERANCE,
    minRatio = GROUND_MIN_RATIO,
    maxRatio = GROUND_MAX_RATIO,
  } = options;
  if (points.length < 3 || points.length > 4) return false;
  return checkIsRectangle([...points], toleranceDeg, minRatio, maxRatio);
};

/**
 * Returns true when the given 3 or 4 **GPS** `{ latitude, longitude }` points describe
 * a rectangle whose aspect ratio is between `minRatio` and `maxRatio`.
 * Coordinates are projected to a flat metric plane before the check.
 *
 * @param points  3 or 4 `{ latitude, longitude }` pairs (any order).
 * @param options toleranceDeg (default 2), minRatio (default 1/3), maxRatio (default 3).
 */
export const isRectangleGPS = (
  points: ThreeOrFour<GPSPoint>,
  options: RectangleOptions = {},
): boolean => {
  const {
    toleranceDeg = GROUND_MAX_ANGLE_TOLERANCE,
    minRatio = GROUND_MIN_RATIO,
    maxRatio = GROUND_MAX_RATIO,
  } = options;
  if (points.length < 3 || points.length > 4) return false;
  const origin = points[0];
  const projected = points.map((p) => gpsToMetric(origin, p)) as ThreeOrFour<Point2D>;
  return checkIsRectangle([...projected], toleranceDeg, minRatio, maxRatio);
};
